/**
 * <hero-video-reel> — plays a sequence of clips in the <video> it wraps and
 * crossfades straight into the next clip with no pause, then loops.
 * A second, hidden <video> preloads the upcoming clip so the switch is seamless.
 * The hidden one carries data-reel-standby so the pause button ignores it.
 * data-playlist: clip URLs separated by "|", in order (desktop).
 * data-playlist-mobile: optional list used below 768px.
 * data-fade: optional crossfade length in ms (default 1400).
 * data-framing: optional per-clip framing, "part-of-url=object-position" pairs
 *   separated by ";" (e.g. "abc123=center top"), for clips that need a different crop.
 * data-trim: optional per-clip length cap in seconds, "part-of-url=seconds" pairs
 *   separated by ";", so only the start of a long clip plays.
 * data-reel-group: optional name; visible reels in the same group switch clips
 *   together, so clip N on each side always plays at the same time. A slot ends
 *   when the first clip in it finishes.
 */
class HeroVideoReel extends HTMLElement {
  static groups = new Map();
  #videos = [];
  #clips = [];
  #index = 0;
  #active = 0;
  #switching = false;
  #fade = 1400;
  #onTimeUpdate = () => this.#maybeSwitch();
  #group = null;
  #onEnded = () => this.#requestSwitch();
  #framing = [];
  #trim = [];

  #setSrc(video, url) {
    if (video.getAttribute('src') !== url) video.src = url;
    const match = this.#framing.find(([key]) => url.includes(key));
    video.style.objectPosition = match ? match[1] : '';
  }
  connectedCallback() {
    const first = this.querySelector('video');
    if (!first) return;
    const isMobile = !window.matchMedia('(min-width: 768px)').matches;
    const list = (isMobile && this.dataset.playlistMobile) || this.dataset.playlist || '';
    this.#clips = list.split('|').map((s) => s.trim()).filter(Boolean);
    if (this.#clips.length === 0) return;
    this.#fade = Number(this.dataset.fade) || 1400;
    this.#framing = (this.dataset.framing || '').split(';').map((p) => p.split('=').map((s) => s.trim())).filter(([k, v]) => k && v);
    this.#trim = (this.dataset.trim || '').split(';').map((p) => p.split('=').map((s) => s.trim())).filter(([k, v]) => k && Number(v) > 0);

    this.#setSrc(first, this.#clips[0]);

    if (this.#clips.length === 1) {
      first.loop = true;
      if (first.autoplay) first.play().catch(() => {});
      return;
    }

    this.#group = this.dataset.reelGroup || null;
    if (this.#group) {
      if (!HeroVideoReel.groups.has(this.#group)) HeroVideoReel.groups.set(this.#group, new Set());
      HeroVideoReel.groups.get(this.#group).add(this);
    }

    first.loop = false;
    const second = first.cloneNode(false);
    second.removeAttribute('autoplay');
    second.preload = 'auto';
    this.#setSrc(second, this.#clips[1]);
    first.after(second);

    this.#videos = [first, second];
    this.#videos.forEach((video, i) => {
      Object.assign(video.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        transition: `opacity ${this.#fade}ms ease-in-out`,
        opacity: i === 0 ? '1' : '0',
      });
      video.toggleAttribute('data-reel-standby', i !== 0);
      video.addEventListener('timeupdate', this.#onTimeUpdate);
      video.addEventListener('ended', this.#onEnded);
    });
    if (first.autoplay) first.play().catch(() => {});
  }

  disconnectedCallback() {
    if (this.#group) HeroVideoReel.groups.get(this.#group)?.delete(this);
    this.#videos.forEach((video) => {
      video.removeEventListener('timeupdate', this.#onTimeUpdate);
      video.removeEventListener('ended', this.#onEnded);
    });
  }

  #maybeSwitch() {
    const current = this.#videos[this.#active];
    if (!current || !current.duration || current.paused) return;
    const src = current.getAttribute('src') || '';
    const cap = this.#trim.find(([key]) => src.includes(key));
    const end = cap ? Math.min(current.duration, Number(cap[1])) : current.duration;
    if (end - current.currentTime <= this.#fade / 1000) this.#requestSwitch();
  }

  get #visible() {
    return this.offsetParent !== null;
  }

  #requestSwitch() {
    if (!this.#visible) return;
    if (!this.#group) {
      this.#switch();
      return;
    }
    HeroVideoReel.groups.get(this.#group)?.forEach((reel) => {
      if (reel.#visible) reel.#switch();
    });
  }

  #switch() {
    if (this.#switching || this.#videos.length < 2) return;
    this.#switching = true;
    const current = this.#videos[this.#active];
    const next = this.#videos[1 - this.#active];

    next.currentTime = 0;
    next.toggleAttribute('data-reel-standby', false);
    next.play().catch(() => {});
    next.style.opacity = '1';
    current.style.opacity = '0';

    window.setTimeout(() => {
      current.pause();
      current.toggleAttribute('data-reel-standby', true);
      this.#index = (this.#index + 1) % this.#clips.length;
      this.#setSrc(current, this.#clips[(this.#index + 1) % this.#clips.length]);
      current.load();
      this.#active = 1 - this.#active;
      this.#switching = false;
    }, this.#fade);
  }
}

if (!customElements.get('hero-video-reel')) {
  customElements.define('hero-video-reel', HeroVideoReel);
}
